import {Component, computed, effect, OnInit, signal} from '@angular/core';
import {Button} from 'primeng/button';
import {AdminService, CountryDetails, LocationDetails} from '../../../api';
import {LoadingService} from '../../../services/loading.service';
import {TableModule} from 'primeng/table';
import {TaAppStore} from '../../../state/clcta-store';
import {Card} from 'primeng/card';
import {FormsModule} from '@angular/forms';
import {InputText} from 'primeng/inputtext';
import {DialogService, DynamicDialogRef} from 'primeng/dynamicdialog';
import {ManageLocationModalComponent} from '../../components/admin/manage-location-modal/manage-location-modal.component';
import {Tag} from 'primeng/tag';
import {ErrorMessageService} from '../../../services/error-message.service';
import {toLocationCategoriesText} from '../../../util/app.types';
import {ScrollHeightDirective} from '../../directives/scroll-height.directive';
import {Tab, TabList, TabPanel, TabPanels, Tabs} from 'primeng/tabs';
import {ManageCountryModalComponent} from '../../components/admin/manage-country-modal/manage-country-modal.component';

@Component({
  selector: 'user-admin',
  imports: [
    Button,
    TableModule,
    Card,
    FormsModule,
    InputText,
    Tag,
    ScrollHeightDirective,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
  ],
  templateUrl: './clc-locations-admin.html',
  styleUrl: './clc-locations-admin.scss',
  providers: [DialogService],
})
export class ClcLocationsAdmin implements OnInit {
  locationList = signal<LocationDetails[]>([]);
  filterInput = signal('');
  filterStr = signal('');
  private debounceTimer: ReturnType<typeof setTimeout> | undefined;

  hasFilter = computed(() => this.filterStr().trim().length > 0);

  filteredLocations = computed(() => {
    const filter = this.filterStr().trim().toLowerCase();
    if (!filter) return [...this.locationList()].sort((a, b) => this.getDefaultName(a).localeCompare(this.getDefaultName(b)));
    return this.locationList()
      .filter((loc) => {
        const nameMatch = loc.names?.some((n) => n.name?.toLowerCase().includes(filter));
        const categoryMatch = loc.category?.toLowerCase().includes(filter);
        return nameMatch || categoryMatch;
      })
      .sort((a, b) => this.getDefaultName(a).localeCompare(this.getDefaultName(b)));
  });

  tabOption = signal<'locations' | 'countries'>('locations');

  countryList = signal<CountryDetails[]>([]);
  countryFilterInput = signal('');
  countryFilterStr = signal('');
  private countryDebounceTimer: ReturnType<typeof setTimeout> | undefined;

  hasCountryFilter = computed(() => this.countryFilterStr().trim().length > 0);

  filteredCountries = computed(() => {
    const filter = this.countryFilterStr().trim().toLowerCase();
    return this.countryList()
      .filter((c) => !filter || c.name?.toLowerCase().includes(filter))
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
  });

  ref: DynamicDialogRef | null = null;

  constructor(
    private adminApi: AdminService,
    private loadingService: LoadingService,
    private store: TaAppStore,
    private dialogService: DialogService,
    private errorMessageService: ErrorMessageService
  ) {
    effect(() => {
      const value = this.filterInput();
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.filterStr.set(value), 200);
    });
    effect(() => {
      const value = this.countryFilterInput();
      clearTimeout(this.countryDebounceTimer);
      this.countryDebounceTimer = setTimeout(() => this.countryFilterStr.set(value), 200);
    });
  }

  ngOnInit(): void {
    this.loadLocations();
    this.loadCountries();
    this.store.updateBreadCrumbData({
      title: 'Locations',
      titleIcon: 'fa-regular fa-map-location-dot fa-lg',
    });
  }

  loadLocations(): void {
    this.loadingService.showLoading({message: 'Loading Locations'});

    this.adminApi.getLocations().subscribe({
      next: (resp) => {
        this.locationList.set(resp.data!);
        this.loadingService.hideLoading();
      },
      error: (error) => {
        console.log('error loading locations: ', error);
        this.loadingService.hideLoading();
      },
    });
  }

  loadCountries(): void {
    this.adminApi.getCountries().subscribe({
      next: (resp) => this.countryList.set(resp.data ?? []),
      error: (error) => console.log('error loading countries: ', error),
    });
  }

  getDefaultName(location: LocationDetails): string {
    if (!location.names?.length) return '-';
    const match = location.names.find((n) => n.nameId === location.defaultNameId);
    return match?.name ?? location.names[0]?.name ?? '-';
  }

  getAltNames(location: LocationDetails): string {
    if (!location.names?.length) return '';
    const defaultId = location.defaultNameId;
    return location.names
      .filter((n) => n.nameId !== defaultId)
      .map((n) => n.enabled ? n.name : n.name + ' (disabled)')
      .sort((a, b) => (a ?? '').localeCompare((b ?? '')))
      .join(', ');
  }

  clear(): void {
    this.filterInput.set('');
    this.filterStr.set('');
  }

  clearCountryFilter(): void {
    this.countryFilterInput.set('');
    this.countryFilterStr.set('');
  }

  openEditLocationModal(location: LocationDetails): void {
    this.ref = this.dialogService.open(ManageLocationModalComponent, {
      header: 'Edit Location',
      inputValues: {
        modalData: location,
        countries: this.countryList(),
      },
      contentStyle: {
        overflow: 'auto',
      },
      modal: true,
      width: 'fit',
    });
    this.ref?.onClose.subscribe((data: LocationDetails | undefined) => {
      if (!data) return;

      this.adminApi.updateCreateLocation(data).subscribe({
        next: (response) => {
          if (!response.success || !response.data) {
            console.error('Error updating location', response.error);
            this.errorMessageService.showTemporaryError('Editing location error. ' + (response.error ?? ''));
            return;
          }
          this.locationList.update((list) =>
            list.map((l) => (l.id === response.data!.id ? {...response.data!, names: response.data?.names?.map((n) => ({...n})) ?? []} : l)),
          );
          console.log(this.locationList().filter((l) => l.id === response.data!.id));
        },
        error: (error) => {
          console.error('Error updating location', error);
          this.errorMessageService.showTemporaryError('Editing location failed. ' + (error.statusText ?? ''));
        },
      });
    });
  }

  openAddLocationModal(): void {
    this.ref = this.dialogService.open(ManageLocationModalComponent, {
      header: 'Add Location',
      inputValues: {
        countries: this.countryList(),
      },
      contentStyle: {
        overflow: 'auto',
      },
      modal: true,
      width: 'fit',
    });
    this.ref?.onClose.subscribe((data: LocationDetails | undefined) => {
      if (!data) return;

      // Optimistic update
      // this.locationList.update((list) => [...list, data]);


      // so the request is deserialized properly
      if (data.id == 'new'){
        data.defaultNameId = null
        data.names![0].locationId = '00000000-0000-0000-0000-000000000000';
      }

      this.adminApi.updateCreateLocation(data).subscribe({
        next: (response) => {
          if (response.success && !!response.data) {
            const exists = this.locationList().some((l) => l.id === response.data!.id);
            if (exists) {
              this.locationList.update((list) => list.map((l) => (l.id === response.data!.id ? response.data! : l)));
            } else {
              this.locationList.update((list) => [...list, response.data!]);
            }
          } else {
            console.error('Error creating location', response.error);
            this.errorMessageService.showTemporaryError('Creating location failed. ' + (response.error ?? ''));
          }
        },
        error: (error) => {
          console.error('Error creating location', error);
          this.errorMessageService.showTemporaryError('Creating location failed. ' + (error.statusText ?? ''));
        },
      });
    });
  }

  openCountryModal(country?: CountryDetails): void {
    this.ref = this.dialogService.open(ManageCountryModalComponent, {
      header: country ? 'Edit Country' : 'Add Country',
      inputValues: country ? {modalData: country} : {},
      contentStyle: {
        overflow: 'auto',
      },
      modal: true,
      width: 'fit',
    });
    this.ref?.onClose.subscribe((data: CountryDetails | undefined) => {
      if (!data) return;

      const action = data.id === 'new' ? 'Creating' : 'Editing';
      this.adminApi.updateCreateCountry(data).subscribe({
        next: (response) => {
          if (!response.success || !response.data) {
            console.error(`Error ${action.toLowerCase()} country`, response.error);
            this.errorMessageService.showTemporaryError(`${action} country failed. ` + (response.error ?? ''));
            return;
          }
          const saved = response.data;
          this.countryList.update((list) =>
            list.some((c) => c.id === saved.id) ? list.map((c) => (c.id === saved.id ? saved : c)) : [...list, saved]);
        },
        error: (error) => {
          console.error(`Error ${action.toLowerCase()} country`, error);
          this.errorMessageService.showTemporaryError(`${action} country failed. ` + (error.statusText ?? ''));
        },
      });
    });
  }

  protected readonly toLocationCategoriesText = toLocationCategoriesText;
}
